import { Grid, Typography } from '@mui/material';
import {
  getCollectionsDocumentsIndicatorsV2,
  getCollectionsManagementsIndicators,
  getCollectionsRecoveryIndicators,
} from 'lib/api/collections';
import {
  IndicatorsCollectionManagement,
  IndicatorsDocument,
  IndicatorsRecovery,
} from 'lib/api/collections/types';
import { SelectInput } from 'lib/common/Inputs';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart, {
  CentralPieChartItem,
} from 'lib/designSystem/Charts/CentralPieChart';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomToggleButtonGroup, {
  ToggleOption,
} from 'lib/designSystem/ToggleButtonGroup';
import { CHART_COLOR_PALETTES, CHART_COLORS } from 'lib/functions/const/colors';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import AmountsDialog from './components/AmountsDialog';
import ByExecutiveCharts from './components/ByExecutiveCharts';
import DetailsDialog from './components/DetailsDialog';
import DocumentsDialog from './components/DocumentsDialog';
import ManagementsDialog from './components/ManagementsDialog';
import RecoveryDialog from './components/RecoveryDialog';

const boxStyle = {
  height: '300px',
  paddingTop: '10px',
};

const smallBoxStyle = {
  height: '250px',
  paddingTop: '10px',
};

interface CustomToggleWrapperProps {
  value: string;
  onChange: (value: string) => void;
  options: ToggleOption[];
}

export const CustomToggleWrapper: React.FC<CustomToggleWrapperProps> = ({
  value,
  onChange,
  options,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <CustomToggleButtonGroup
        value={value}
        onChange={(v: string) => onChange(v)}
        options={options}
      />
    </div>
  );
};

interface FiltersProps {
  selectedArrearTrench: string;
  selectedState: string;
  selectedSubStatus: string;
  selectedProduct: string;
  selectedBusiness: string;
  selectedStakeholder: string;
  selectedCollector: string;
  selecteCommercialExecutive: string;
  setSelectedArrearTrench: (value: string) => void;
  setSelectedState: (value: string) => void;
  setSelectedSubStatus: (value: string) => void;
  setSelectedProduct: (value: string) => void;
  setSelectedBusiness: (value: string) => void;
  setSelectedStakeholder: (value: string) => void;
  setSelectedCollector: (value: string) => void;
  setSelecteCommercialExecutive: (value: string) => void;
  documents: IndicatorsDocument[];
}

const Filters: React.FC<FiltersProps> = ({
  selectedArrearTrench,
  selectedState,
  selectedSubStatus,
  selectedProduct,
  selectedBusiness,
  selectedStakeholder,
  selectedCollector,
  selecteCommercialExecutive,
  setSelectedArrearTrench,
  setSelectedState,
  setSelectedSubStatus,
  setSelectedProduct,
  setSelectedBusiness,
  setSelectedStakeholder,
  setSelectedCollector,
  setSelecteCommercialExecutive,
  documents,
}) => {
  const toOptions = (set: Set<string>) => [
    { value: 'all', label: 'Todos' },
    ...Array.from(set).map((value) => ({ value, label: value })),
  ];

  const filterOptions = documents.reduce(
    (acc, doc) => {
      acc.arrearTrenches.add(doc.arrearTrench);
      acc.states.add(doc.documentState);
      acc.subStatuses.add(doc.lastCollectionManagementSubState);
      acc.products.add(doc.productEsp);
      acc.businesses.add(doc.businessName);
      acc.stakeholders.add(doc.stakeholderName);
      acc.collectors.add(doc.collectorName);
      acc.commercialExecutives.add(doc.commercialExecutiveName);
      return acc;
    },
    {
      arrearTrenches: new Set<string>(),
      states: new Set<string>(),
      subStatuses: new Set<string>(),
      products: new Set<string>(),
      businesses: new Set<string>(),
      stakeholders: new Set<string>(),
      collectors: new Set<string>(),
      commercialExecutives: new Set<string>(),
    },
  );

  const arrearTrenches = toOptions(filterOptions.arrearTrenches);
  const states = toOptions(filterOptions.states);
  const subStatuses = toOptions(filterOptions.subStatuses);
  const products = toOptions(filterOptions.products);
  const businesses = toOptions(filterOptions.businesses);
  const stakeholders = toOptions(filterOptions.stakeholders);
  const collectors = toOptions(filterOptions.collectors);
  const commercialExecutives = toOptions(filterOptions.commercialExecutives);

  return (
    <TableCard sx={{ marginBottom: '16px' }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <SelectInput
            label="Tramo vencimiento"
            value={selectedArrearTrench}
            onChange={(value) => setSelectedArrearTrench(value || 'all')}
            options={arrearTrenches}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Estado"
            value={selectedState}
            onChange={(value) => setSelectedState(value || 'all')}
            options={states}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Subestado"
            value={selectedSubStatus}
            onChange={(value) => setSelectedSubStatus(value || 'all')}
            options={subStatuses}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Producto"
            value={selectedProduct}
            onChange={(value) => setSelectedProduct(value || 'all')}
            options={products}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Cliente"
            value={selectedBusiness}
            onChange={(value) => setSelectedBusiness(value || 'all')}
            options={businesses}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Deudor"
            value={selectedStakeholder}
            onChange={(value) => setSelectedStakeholder(value || 'all')}
            options={stakeholders}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Ejecutivo cobranza"
            value={selectedCollector}
            onChange={(value) => setSelectedCollector(value || 'all')}
            options={collectors}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectInput
            label="Ejecutivo comercial"
            value={selecteCommercialExecutive}
            onChange={(value) => setSelecteCommercialExecutive(value || 'all')}
            options={commercialExecutives}
          />
        </Grid>
      </Grid>
    </TableCard>
  );
};

function getColorByArrearTrench(arrearTrench: string): {
  color: string;
  palette: string[];
} {
  switch (arrearTrench) {
    case 'Vigente':
      return { color: CHART_COLORS.green, palette: CHART_COLOR_PALETTES.green };
    case '1 a 30 días':
      return { color: CHART_COLORS.amber, palette: CHART_COLOR_PALETTES.amber };
    case '31 a 60 días':
      return {
        color: CHART_COLORS.orange,
        palette: CHART_COLOR_PALETTES.orange,
      };
    case '61 a 90 días':
      return { color: CHART_COLORS.red, palette: CHART_COLOR_PALETTES.red };
    case 'Más de 90 días':
      return {
        color: CHART_COLORS.burgundy,
        palette: CHART_COLOR_PALETTES.burgundy,
      };
    case 'Venció hace menos de 7 días':
      return { color: CHART_COLORS.red, palette: CHART_COLOR_PALETTES.red };
    case 'Venció hace más de 7 días':
      return {
        color: CHART_COLORS.burgundy,
        palette: CHART_COLOR_PALETTES.burgundy,
      };
    case 'Vence en más de 7 días':
      return { color: CHART_COLORS.green, palette: CHART_COLOR_PALETTES.green };
    case 'Vence en menos de 7 días':
      return { color: CHART_COLORS.amber, palette: CHART_COLOR_PALETTES.amber };
    default:
      return { color: '', palette: [] };
  }
}

const CollectionsIndicatorsV2 = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [showDocumentType, setShowDocumentType] = useState('amount');
  const [showDueBy, setShowDueBy] = useState('business');
  const [selectedSegment, setSelectedSegment] =
    useState<CentralPieChartItem | null>(null);
  const [documentsForInsights, setDocumentsForInsights] = useState<
    IndicatorsDocument[]
  >([]);
  const [showRecoveryBy, setShowRecoveryBy] = useState('amount');
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState(false);
  const [openAmountsDialog, setOpenAmountsDialog] = useState(false);
  const [openManagementsDialog, setOpenManagementsDialog] = useState(false);
  const [managementsForInsights, setManagementsForInsights] = useState<
    IndicatorsCollectionManagement[]
  >([]);
  const [recoveriesForInsights, setRecoveriesForInsights] = useState<
    IndicatorsRecovery[]
  >([]);
  const [openRecoveryDialog, setOpenRecoveryDialog] = useState(false);
  const [canHaveSubordinates, setCanHaveSubordinates] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState<string>('');
  const [detailsData, setDetailsData] = useState<
    { name: string; value: number }[]
  >([]);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  // Filters
  const [selectedArrearTrench, setSelectedArrearTrench] =
    useState<string>('all');
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedSubStatus, setSelectedSubStatus] = useState<string>('all');
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [selectedBusiness, setSelectedBusiness] = useState<string>('all');
  const [selectedStakeholder, setSelectedStakeholder] = useState<string>('all');
  const [selectedCollector, setSelectedCollector] = useState<string>('all');
  const [selecteCommercialExecutive, setSelecteCommercialExecutive] =
    useState<string>('all');

  const managementsQuery = getCollectionsManagementsIndicators(dispatch);
  const recoveryQuery = getCollectionsRecoveryIndicators(dispatch);
  const documentsV2Query = getCollectionsDocumentsIndicatorsV2(dispatch);

  const loading =
    managementsQuery.isPending ||
    recoveryQuery.isPending ||
    documentsV2Query.isPending;

  const managementsData = managementsQuery.data?.collectionManagements ?? [];

  const recoveryData = recoveryQuery.data?.documents ?? [];

  const documentsV2DataNotFiltered = documentsV2Query.data?.documents ?? [];

  const documentsV2Data = documentsV2DataNotFiltered.filter((document) => {
    const matchArrearTrench =
      selectedArrearTrench === 'all' ||
      selectedArrearTrench === document.arrearTrench;
    const matchState =
      selectedState === 'all' || selectedState === document.documentState;
    const matchSubStatus =
      selectedSubStatus === 'all' ||
      selectedSubStatus === document.lastCollectionManagementSubState;
    const matchProduct =
      selectedProduct === 'all' || selectedProduct === document.productEsp;
    const matchBusiness =
      selectedBusiness === 'all' || selectedBusiness === document.businessName;
    const matchStakeholder =
      selectedStakeholder === 'all' ||
      selectedStakeholder === document.stakeholderName;
    const matchCollector =
      selectedCollector === 'all' ||
      selectedCollector === document.collectorName;
    const matchCommercialExecutive =
      selecteCommercialExecutive === 'all' ||
      selecteCommercialExecutive === document.commercialExecutiveName;

    return (
      matchArrearTrench &&
      matchState &&
      matchSubStatus &&
      matchProduct &&
      matchBusiness &&
      matchStakeholder &&
      matchCollector &&
      matchCommercialExecutive
    );
  });

  // Single pass over documentsV2Data to build all document-based aggregates
  const documentsAggregates = documentsV2Data.reduce(
    (
      acc: {
        amountByArrearTrench: Record<string, { amount: number; count: number }>;
        byState: Record<string, number>;
        byTrench: Record<string, number>;
        bySubStatus: Record<string, number>;
        byProduct: Record<string, number>;
        duedByBusiness: Record<string, { amount: number; count: number }>;
        duedByStakeholder: Record<string, { amount: number; count: number }>;
      },
      doc: IndicatorsDocument,
    ) => {
      const {
        arrearTrench,
        documentState,
        lastCollectionManagementSubState,
        productEsp,
        businessName,
        stakeholderName,
      } = doc;
      const isDued = arrearTrench !== 'Vigente';

      // Amount/count by arrear trench
      if (acc.amountByArrearTrench[arrearTrench]) {
        acc.amountByArrearTrench[arrearTrench].amount += doc.amount;
        acc.amountByArrearTrench[arrearTrench].count += 1;
      } else {
        acc.amountByArrearTrench[arrearTrench] = {
          amount: doc.amount,
          count: 1,
        };
      }

      // Saldo by state, trench, subStatus, product
      const addToRecord = (
        record: Record<string, number>,
        key: string,
        value: number,
      ) => {
        record[key] = (record[key] ?? 0) + value;
      };
      addToRecord(acc.byState, documentState, doc.documentRemainingAmount);
      addToRecord(acc.byTrench, arrearTrench, doc.documentRemainingAmount);
      addToRecord(
        acc.bySubStatus,
        lastCollectionManagementSubState,
        doc.documentRemainingAmount,
      );
      addToRecord(acc.byProduct, productEsp, doc.documentRemainingAmount);

      if (isDued) {
        const addDued = (
          record: Record<string, { amount: number; count: number }>,
          key: string,
          amount: number,
        ) => {
          if (record[key]) {
            record[key].amount += amount;
            record[key].count += 1;
          } else {
            record[key] = { amount, count: 1 };
          }
        };
        addDued(
          acc.duedByBusiness,
          businessName,
          doc.documentRemainingAmountNet,
        );
        addDued(
          acc.duedByStakeholder,
          stakeholderName,
          doc.documentRemainingAmount,
        );
      }

      return acc;
    },
    {
      amountByArrearTrench: {},
      byState: {},
      byTrench: {},
      bySubStatus: {},
      byProduct: {},
      duedByBusiness: {},
      duedByStakeholder: {},
    },
  );

  const amountByArrearTrenchChartData = Object.entries(
    documentsAggregates.amountByArrearTrench,
  ).map(([name, value]) => ({
    name,
    value: value.amount,
    color: getColorByArrearTrench(name).color,
    colorPalette: getColorByArrearTrench(name).palette,
  }));

  const countByArrearTrenchChartData = Object.entries(
    documentsAggregates.amountByArrearTrench,
  ).map(([name, value]) => ({
    name,
    value: value.count,
    color: getColorByArrearTrench(name).color,
    colorPalette: getColorByArrearTrench(name).palette,
  }));

  const totalDuedAmountBusiness = Object.values(
    documentsAggregates.duedByBusiness,
  ).reduce((sum, x) => sum + x.amount, 0);
  const totalDuedAmountStakeholder = Object.values(
    documentsAggregates.duedByStakeholder,
  ).reduce((sum, x) => sum + x.amount, 0);

  const duedByBusinessChartDataSourceProcessed = Object.entries(
    documentsAggregates.duedByBusiness,
  ).reduce(
    (acc: Record<string, { amount: number; count: number }>, [name, value]) => {
      if (
        totalDuedAmountBusiness > 0 &&
        value.amount / totalDuedAmountBusiness > 0.05
      ) {
        acc[name] = value;
      } else {
        acc['Otros'] = {
          amount: (acc['Otros']?.amount || 0) + value.amount,
          count: (acc['Otros']?.count || 0) + value.count,
        };
      }
      return acc;
    },
    {},
  );

  const duedByBusinessChartData = Object.entries(
    duedByBusinessChartDataSourceProcessed,
  ).map(([name, value]) => ({
    name,
    value: value.amount,
  }));

  const duedByStakeholderChartDataSourceProcessed = Object.entries(
    documentsAggregates.duedByStakeholder,
  ).reduce(
    (acc: Record<string, { amount: number; count: number }>, [name, value]) => {
      if (
        totalDuedAmountStakeholder > 0 &&
        value.amount / totalDuedAmountStakeholder > 0.05
      ) {
        acc[name] = value;
      } else {
        acc['Otros'] = {
          amount: (acc['Otros']?.amount || 0) + value.amount,
          count: (acc['Otros']?.count || 0) + value.count,
        };
      }
      return acc;
    },
    {},
  );

  const duedByStakeholderChartData = Object.entries(
    duedByStakeholderChartDataSourceProcessed,
  ).map(([name, value]) => ({
    name,
    value: value.amount,
  }));

  const byStateChartData = Object.entries(documentsAggregates.byState).map(
    ([name, value]) => ({ name, value }),
  );

  const byTrenchChartData = Object.entries(documentsAggregates.byTrench).map(
    ([name, value]) => ({
      name,
      value,
      color: getColorByArrearTrench(name).color,
      colorPalette: getColorByArrearTrench(name).palette,
    }),
  );

  const bySubStatusChartData = Object.entries(
    documentsAggregates.bySubStatus,
  ).map(([name, value]) => ({ name, value }));

  const byProductChartData = Object.entries(documentsAggregates.byProduct).map(
    ([name, value]) => ({ name, value }),
  );

  const managementsByTrenchChartDataSource = managementsData.reduce(
    (
      acc: Record<string, { count: number }>,
      management: IndicatorsCollectionManagement,
    ) => {
      const trench = management.trench;
      if (acc[trench]) {
        acc[trench] = { count: acc[trench].count + 1 };
      } else {
        acc[trench] = { count: 1 };
      }
      return acc;
    },
    {},
  );

  const managementsByTrenchChartData = Object.entries(
    managementsByTrenchChartDataSource,
  ).map(([name, value]) => ({
    name,
    value: value.count,
    color: getColorByArrearTrench(name).color,
    colorPalette: getColorByArrearTrench(name).palette,
  }));

  const recoveryByCollectorChartDataSource = recoveryData.reduce(
    (
      acc: Record<string, { count: number; amount: number }>,
      recovery: IndicatorsRecovery,
    ) => {
      const collectorName = recovery.collectorName;
      if (acc[collectorName]) {
        acc[collectorName] = {
          count: acc[collectorName].count + 1,
          amount: acc[collectorName].amount + recovery.amount,
        };
      } else {
        acc[collectorName] = { count: 1, amount: recovery.amount };
      }
      return acc;
    },
    {},
  );

  const recoveryByCollectorChartData = Object.entries(
    recoveryByCollectorChartDataSource,
  ).map(([name, value]) => ({
    name,
    value: value.amount,
  }));

  const countByCollectorChartData = Object.entries(
    recoveryByCollectorChartDataSource,
  ).map(([name, value]) => ({
    name,
    value: value.count,
    color: CHART_COLORS.green,
    colorPalette: CHART_COLOR_PALETTES.green,
  }));

  const handleDialogClose = () => {
    setOpenDocumentsDialog(false);
    setOpenAmountsDialog(false);
    setOpenManagementsDialog(false);
    setOpenRecoveryDialog(false);
    setOpenDetailsDialog(false);
    setSelectedSegment(null);
    setDocumentsForInsights([]);
    setRecoveriesForInsights([]);
    setManagementsForInsights([]);
    setDetailsData([]);
    setDetailsTitle('');
  };

  const handleDocumentsPieChartClick = (entry: CentralPieChartItem) => {
    setDocumentsForInsights(
      documentsV2Data.filter(
        (document) => document.arrearTrench === entry.name,
      ),
    );
    setSelectedSegment(entry);
    setOpenDocumentsDialog(true);
  };

  const handleAmountsPieChartClick = (entry: CentralPieChartItem) => {
    setDocumentsForInsights(
      documentsV2Data.filter((document) =>
        showDueBy === 'business'
          ? document.businessName === entry.name
          : document.stakeholderName === entry.name,
      ),
    );
    setSelectedSegment(entry);
    setOpenAmountsDialog(true);
  };

  const handleManagementsPieChartClick = (entry: CentralPieChartItem) => {
    setManagementsForInsights(
      managementsData.filter((management) => management.trench === entry.name),
    );
    setSelectedSegment(entry);
    setOpenManagementsDialog(true);
  };

  const handleRecoveryPieChartClick = (entry: CentralPieChartItem) => {
    setRecoveriesForInsights(
      recoveryData.filter((recovery) => recovery.collectorName === entry.name),
    );
    setSelectedSegment(entry);
    setOpenRecoveryDialog(true);
  };

  const handleClickArrearTrench = (_: CentralPieChartItem) => {
    setDetailsTitle('Saldo documentos por tramo vencimiento');
    setDetailsData(byTrenchChartData);
    setOpenDetailsDialog(true);
  };

  const handleClickState = (_: CentralPieChartItem) => {
    setDetailsTitle('Saldo documentos por estado');
    setDetailsData(byStateChartData);
    setOpenDetailsDialog(true);
  };

  const handleClickSubStatus = (_: CentralPieChartItem) => {
    setDetailsTitle('Saldo documentos por subestado cobranza');
    setDetailsData(bySubStatusChartData);
    setOpenDetailsDialog(true);
  };

  const handleClickProduct = (_: CentralPieChartItem) => {
    setDetailsTitle('Saldo documentos por producto');
    setDetailsData(byProductChartData);
    setOpenDetailsDialog(true);
  };

  useEffect(() => {
    const boss = validatePermissions(user, 'have_subordinates');
    const admin = validatePermissions(user, 'edit_subordinates');
    setCanHaveSubordinates(boss || admin);
  }, [user]);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div style={{ padding: '16px 0px' }}>
      <Filters
        selectedArrearTrench={selectedArrearTrench}
        selectedState={selectedState}
        selectedSubStatus={selectedSubStatus}
        selectedProduct={selectedProduct}
        selectedBusiness={selectedBusiness}
        selectedStakeholder={selectedStakeholder}
        selectedCollector={selectedCollector}
        selecteCommercialExecutive={selecteCommercialExecutive}
        setSelectedArrearTrench={setSelectedArrearTrench}
        setSelectedState={setSelectedState}
        setSelectedSubStatus={setSelectedSubStatus}
        setSelectedProduct={setSelectedProduct}
        setSelectedBusiness={setSelectedBusiness}
        setSelectedStakeholder={setSelectedStakeholder}
        setSelectedCollector={setSelectedCollector}
        setSelecteCommercialExecutive={setSelecteCommercialExecutive}
        documents={documentsV2DataNotFiltered}
      />
      <Grid container spacing={2} style={{ marginBottom: '16px' }}>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={boxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                {showDocumentType === 'count' ? 'Documentos' : 'Monto'}
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                por tramo vencimiento
              </Typography>
              <div style={{ padding: '6px 0px' }}>
                <CentralPieChart
                  data={
                    showDocumentType === 'count'
                      ? countByArrearTrenchChartData
                      : amountByArrearTrenchChartData
                  }
                  showLegend={false}
                  height={220}
                  onClick={handleDocumentsPieChartClick}
                />
              </div>
              <CustomToggleWrapper
                value={showDocumentType}
                onChange={setShowDocumentType}
                options={[
                  { value: 'amount', label: 'Monto' },
                  { value: 'count', label: 'Documentos' },
                ]}
              />
            </div>
          </TableCard>
        </Grid>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={boxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                Monto vencido
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                {showDueBy === 'business' ? 'por cliente' : 'por deudor'}
              </Typography>
              <div style={{ padding: '6px 0px' }}>
                <CentralPieChart
                  data={
                    showDueBy === 'business'
                      ? duedByBusinessChartData
                      : duedByStakeholderChartData
                  }
                  showLegend={false}
                  height={220}
                  colors={CHART_COLOR_PALETTES.red}
                  onClick={handleAmountsPieChartClick}
                />
              </div>
              <CustomToggleWrapper
                value={showDueBy}
                onChange={setShowDueBy}
                options={[
                  { value: 'business', label: 'Por cliente' },
                  { value: 'stakeholder', label: 'Por deudor' },
                ]}
              />
            </div>
          </TableCard>
        </Grid>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={boxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                Gestiones
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                por tramo vencimiento
              </Typography>
              <div style={{ padding: '6px 0px' }}>
                <CentralPieChart
                  data={managementsByTrenchChartData}
                  showLegend={false}
                  height={220}
                  onClick={handleManagementsPieChartClick}
                />
              </div>
            </div>
          </TableCard>
        </Grid>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={boxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                {showRecoveryBy === 'count'
                  ? 'Documentos recuperados'
                  : 'Monto recuperado'}
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                {canHaveSubordinates
                  ? 'por ejecutivo en el último mes'
                  : 'en el último mes'}
              </Typography>
              <div style={{ padding: '6px 0px' }}>
                <CentralPieChart
                  data={
                    showRecoveryBy === 'amount'
                      ? recoveryByCollectorChartData
                      : countByCollectorChartData
                  }
                  showLegend={false}
                  height={220}
                  onClick={handleRecoveryPieChartClick}
                  colors={CHART_COLOR_PALETTES.green}
                />
              </div>
              <CustomToggleWrapper
                value={showRecoveryBy}
                onChange={setShowRecoveryBy}
                options={[
                  { value: 'amount', label: 'Monto' },
                  { value: 'count', label: 'Documentos' },
                ]}
              />
            </div>
          </TableCard>
        </Grid>
      </Grid>
      {canHaveSubordinates && (
        <ByExecutiveCharts documentsV2Data={documentsV2Data} />
      )}
      <Grid container spacing={2} style={{ marginTop: '4px' }}>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={smallBoxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                Saldo documentos
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                por tramo vencimiento
              </Typography>
              <CentralPieChart
                data={byTrenchChartData}
                showLegend={false}
                height={220}
                onClick={handleClickArrearTrench}
              />
            </div>
          </TableCard>
        </Grid>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={smallBoxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                Saldo documentos
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                por estado
              </Typography>
              <CentralPieChart
                data={byStateChartData}
                showLegend={false}
                height={220}
                onClick={handleClickState}
              />
            </div>
          </TableCard>
        </Grid>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={smallBoxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                Saldo documentos
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                por subestado cobranza
              </Typography>
              <CentralPieChart
                data={bySubStatusChartData}
                showLegend={false}
                height={220}
                onClick={handleClickSubStatus}
              />
            </div>
          </TableCard>
        </Grid>
        <Grid item xs={3}>
          <TableCard sx={{ padding: '0px 18px 18px 18px' }}>
            <div style={smallBoxStyle}>
              <Typography
                variant="body1"
                textAlign={'center'}
                fontWeight={'bold'}
              >
                Saldo documentos
              </Typography>
              <Typography variant="body2" textAlign={'center'}>
                por producto
              </Typography>
              <CentralPieChart
                data={byProductChartData}
                showLegend={false}
                height={220}
                onClick={handleClickProduct}
              />
            </div>
          </TableCard>
        </Grid>
      </Grid>
      {selectedSegment &&
        documentsForInsights.length > 0 &&
        openDocumentsDialog && (
          <DocumentsDialog
            openDialog={openDocumentsDialog}
            handleDialogClose={handleDialogClose}
            documentsForInsights={documentsForInsights}
            showDocumentType={showDocumentType}
            selectedSegment={selectedSegment}
          />
        )}
      {selectedSegment &&
        documentsForInsights.length > 0 &&
        openAmountsDialog && (
          <AmountsDialog
            openDialog={openAmountsDialog}
            handleDialogClose={handleDialogClose}
            documentsForInsights={documentsForInsights}
            selectedSegment={selectedSegment}
            showDueBy={showDueBy}
          />
        )}
      {selectedSegment &&
        managementsForInsights.length > 0 &&
        openManagementsDialog && (
          <ManagementsDialog
            openDialog={openManagementsDialog}
            handleDialogClose={handleDialogClose}
            managementsForInsights={managementsForInsights}
            selectedSegment={selectedSegment}
          />
        )}
      {selectedSegment &&
        recoveriesForInsights.length > 0 &&
        openRecoveryDialog && (
          <RecoveryDialog
            openDialog={openRecoveryDialog}
            handleDialogClose={handleDialogClose}
            recoveriesForInsights={recoveriesForInsights}
            selectedSegment={selectedSegment}
            showRecoveryBy={showRecoveryBy}
          />
        )}
      {openDetailsDialog && detailsData.length > 0 && (
        <DetailsDialog
          openDialog={openDetailsDialog}
          handleDialogClose={handleDialogClose}
          title={detailsTitle}
          data={detailsData}
        />
      )}
    </div>
  );
};

export default CollectionsIndicatorsV2;
